import React from 'react';
import { useNavigate } from 'react-router-dom';

import { HttpError } from '../../api/client';
import type { MatchingBoardItemDto } from '../../api/matchingTypes';
import {
  formatDateKeyRu,
  groupByDate,
  hasCurrentRequestOnDate,
  isClubMember,
  matchingErrorMessage,
} from '../../domain/matching/matching';
import { MatchingRequestCard } from '../../features/matching/MatchingRequestCard';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectAllGames } from '../../store/selectors/gamesSelectors';
import {
  selectMatchingBoard,
  selectMatchingMeta,
  selectMatchingPanelByDate,
} from '../../store/selectors/matchingSelectors';
import { uiActions } from '../../store/slices/uiSlice';
import {
  cancelMatchingRequest,
  copyMatchingRequest,
  fetchMatchingBoard,
  fetchMatchingMeta,
  fetchMatchingPanel,
} from '../../store/thunks';
import { fetchGames } from '../../store/thunks/gamesThunks';
import { TavernButton, TavernEmptyState, TavernLoading } from '../../ui/components';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import './MatchingBoardPage.css';

type Pending = null | { kind: 'copy' | 'delete'; item: MatchingBoardItemDto };

/** "Сбор": all active search requests for the next seven days, grouped by date. */
export function MatchingBoardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const role = useAppSelector((s) => s.auth.user?.role);
  const board = useAppSelector(selectMatchingBoard);
  const meta = useAppSelector(selectMatchingMeta);
  const panelByDate = useAppSelector(selectMatchingPanelByDate);
  const gamesLoaded = useAppSelector(selectAllGames).length > 0;
  const member = isClubMember(role);

  const [pending, setPending] = React.useState<Pending>(null);
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);

  React.useEffect(() => {
    if (member && !gamesLoaded) void dispatch(fetchGames());
  }, [dispatch, member, gamesLoaded]);

  React.useEffect(() => {
    if (!member) return;
    let cancelled = false;
    void Promise.allSettled([
      dispatch(fetchMatchingMeta()),
      dispatch(fetchMatchingPanel()),
      dispatch(fetchMatchingBoard({ page: 0 })),
    ]).then(() => {
      if (!cancelled) setIsFirstLoad(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, member]);

  const groups = React.useMemo(() => groupByDate(board.items), [board.items]);

  const notifyError = (err: unknown, fallback: string) => {
    const code = err instanceof HttpError ? err.code : undefined;
    const message = err instanceof Error ? err.message : fallback;
    dispatch(uiActions.setNotification({ type: 'error', message: matchingErrorMessage(code, message) }));
  };

  const onConfirm = async () => {
    const action = pending;
    setPending(null);
    if (!action) return;
    try {
      if (action.kind === 'copy') {
        await dispatch(copyMatchingRequest({ id: action.item.id })).unwrap();
        dispatch(uiActions.setNotification({ type: 'success', message: 'Заявка создана' }));
      } else {
        await dispatch(cancelMatchingRequest({ id: action.item.id })).unwrap();
      }
    } catch (err) {
      notifyError(err, 'Не удалось выполнить действие');
    }
  };

  if (!member) {
    return (
      <div className="ttbPage matching-board-page">
        <TavernEmptyState icon="🔒" title="Сбор игроков" description="Доступно членам клуба" />
      </div>
    );
  }

  return (
    <div className="ttbPage matching-board-page">
      <div className="matching-board-page__header">
        <div className="matching-board-page__heading">
          <button
            type="button"
            className="ttbButton ttbButtonOutlineHeader matching-board-page__back"
            aria-label="Назад"
            onClick={() => navigate('/floor-plan')}
          >
            ←
          </button>
          <h1 className="ttbPageTitle">
            <span>🎲</span>
            Сбор
          </h1>
        </div>
        <TavernButton
          variant="primary"
          className="matching-board-page__create-button"
          onClick={() => navigate('/gathering/new')}
        >
          Создать заявку
        </TavernButton>
      </div>

      <div className="ttbPageContent matching-board-page__content">
        {isFirstLoad ? <TavernLoading message="Загрузка…" /> : null}

        {!isFirstLoad && groups.length === 0 ? (
          <TavernEmptyState
            icon="🪑"
            title="Пока никто не ищет компанию"
            description="Оставьте заявку — мы соберём стол, когда найдутся игроки"
            action={
              <TavernButton variant="primary" onClick={() => navigate('/gathering/new')}>
                Создать заявку
              </TavernButton>
            }
          />
        ) : null}

        {groups.map((group) => (
          <section key={group.date} className="matching-board-page__day">
            <h2 className="matching-board-page__day-title">{formatDateKeyRu(group.date)}</h2>
            {group.items.map((item) => (
              <MatchingRequestCard
                key={item.id}
                item={item}
                meta={meta}
                alreadyHasRequest={hasCurrentRequestOnDate(panelByDate, item.gameDate)}
                onEdit={(it) => navigate(`/gathering/${it.id}/edit`, { state: { item: it } })}
                onDelete={(it) => setPending({ kind: 'delete', item: it })}
                onCopy={(it) => setPending({ kind: 'copy', item: it })}
              />
            ))}
          </section>
        ))}

        {board.page + 1 < board.totalPages ? (
          <TavernButton
            variant="secondary"
            disabled={board.isLoading}
            onClick={() => void dispatch(fetchMatchingBoard({ page: board.page + 1 }))}
          >
            {board.isLoading ? 'Загрузка…' : 'Показать ещё'}
          </TavernButton>
        ) : null}
      </div>

      <ConfirmSheet
        open={pending != null}
        title={pending?.kind === 'copy' ? 'Создать такую же заявку?' : 'Удалить заявку?'}
        message={
          pending?.kind === 'copy'
            ? 'Будет создана ваша заявка с той же датой, окном начала и предпочтениями.'
            : 'Заявка будет снята с поиска.'
        }
        confirmLabel={pending?.kind === 'copy' ? 'Создать' : 'Удалить'}
        destructive={pending?.kind === 'delete'}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          void onConfirm();
        }}
      />
    </div>
  );
}
